import { StringKey } from '../lang/index';
import { Company } from './company.types';

export enum ShareClassSortBy {
  CONVERSION_RATIO = 'conversionRatio',
  CREATION_DATE = 'creationDate',
  NAME = 'name',
  STAKEHOLDERS = 'stakeholders',
  VOTING_RIGHT = 'votingRight',
}

export enum ShareClassOrder {
  ASC = 'asc',
  DESC = 'desc',
}
export enum DaysPerYearOption {
  FULL_YEAR = '365',
  ROUNDED = '360',
}

export const itemsToFetchMap = [5, 10, 20, 50, 100];

export type ParticipatingSettings = {
  capValue: number;
} & (
  | { daysPersYear: DaysPerYearOption; interest: number }
  | { daysPersYear?: never; interest?: never }
);

export interface LiquidityPreferences {
  multiple: number;
  participating?: ParticipatingSettings;
  seniority: number;
}

export enum AntiDilutionBase {
  BROAD_BASED_WEIGHTED_AVERAGE = 'bbwa',
  FULL_RATCHET = 'fr',
  NARROW_BASED_WEIGHTED_AVERAGE = 'nbwa',
}

export const antiDilutionBaseTitle: Record<AntiDilutionBase, StringKey> = {
  [AntiDilutionBase.BROAD_BASED_WEIGHTED_AVERAGE]: 'BROAD_BASED_WEIGHTED_AVERAGE',
  [AntiDilutionBase.FULL_RATCHET]: 'FULL_RATCHET',
  [AntiDilutionBase.NARROW_BASED_WEIGHTED_AVERAGE]: 'NARROW_BASED_WEIGHTED_AVERAGE',
};

export const antiDilutionBaseTitleMap = Object.entries(antiDilutionBaseTitle) as [
  AntiDilutionBase,
  StringKey,
][];

export interface AntiDilutionRightsSettings {
  base: AntiDilutionBase;
}

export type ShareClass = {
  additionalNotes: string;
  antiDilutionRights?: AntiDilutionRightsSettings;
  company: Company;
  conversionRatio: number;
  conversionRatioEnabled: boolean;
  createdAt: string;
  creationDate: string;
  dividendRightEnabled: boolean;
  docusignFileLinks?: string[];
  filesLinks: string[];
  id: string;
  isDefault: boolean;
  isUsed: boolean;
  liquidityPreferences?: LiquidityPreferences;
  name: string;
  stakeholderIds?: string[];
  stakeholders?: number;
  totalShares?: number;
  votingRight: number;
  votingRightEnabled: boolean;
};

export type ParticipatingSettingsDto = {
  capValue: number;
  daysPersYear?: DaysPerYearOption;
  interest?: number;
};

export type LiquidityPreferencesDto = {
  multiple: number;
  participating?: null | ParticipatingSettingsDto;
  seniority: number;
};

export type AntiDilutionRightsSettingsDto = {
  base: AntiDilutionBase;
};

export type CreateShareClassDto = {
  additionalNotes?: string;
  antiDilutionRights?: AntiDilutionRightsSettingsDto;
  conversionRatio: number;
  conversionRatioEnabled: boolean;
  creationDate?: string;
  dividendRightEnabled: boolean;
  filesLinks: string[];
  liquidityPreferences?: LiquidityPreferencesDto;
  name: string;
  votingRight: number;
  votingRightEnabled: boolean;
};

export class PatchShareClassDto {
  antiDilutionRights?: AntiDilutionRightsSettingsDto | null;
  conversionRatio?: number;
  conversionRatioEnabled?: boolean;
  creationDate?: string;
  dividendRightEnabled?: boolean;
  filesLinks?: string[];
  additionalNotes?: string;
  liquidityPreferences?: LiquidityPreferencesDto | null;
  name?: string;
  votingRight?: number;
  votingRightEnabled?: boolean;
}

export enum ShareClassesFilters {
  IN_USE = 'in-use',
  NOT_USED = 'not-used',
}

export type FilterVariant = 'all' | ShareClassesFilters;

export const filterVariant: Record<FilterVariant, StringKey> = {
  all: 'FILTER_ALL',
  [ShareClassesFilters.IN_USE]: 'FILTER_IN_USE',
  [ShareClassesFilters.NOT_USED]: 'FILTER_NOT_USED',
};

export const filterVariantMap = Object.entries(filterVariant);
